import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  deriveKitchenCabinetryLinearFeet,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Kitchen Estimator (Phase 5A). Maps every kitchen-specific answer
 * (see packages/config/src/questions/kitchen.ts) onto its own discrete
 * pricing line item(s) - per the brief, "do not simply apply arbitrary
 * percentage multipliers to the entire kitchen cost where a discrete
 * scope allowance is more appropriate." Twelve categories are
 * independently represented in the output, each traceable to its own
 * `explanation.steps` entry: base renovation, cabinetry, countertops,
 * appliances, flooring, backsplash, electrical, plumbing, structural
 * changes, island, demolition, and permits/design.
 *
 * Tiering pattern used throughout: several categories (electrical,
 * plumbing, structural) are gated by a common yes/no/unknown work-type
 * question (e.g. `electrical_changes`) but sized by an optional
 * kitchen-specific follow-up tier question (e.g.
 * `kitchen_electrical_scope`) that only appears once the work-type
 * question says there's relevant work - see kitchen.ts's own doc comment
 * for why. When the tier question wasn't answered (either because the
 * user skipped it, or because a caller handed the engine an
 * EstimatorInput directly without it), the calculator falls back to the
 * original Phase 2 flat allowance for that category rather than guessing
 * a tier or dropping the cost entirely.
 *
 * Post-launch audit fix: every tiered single-select category (cabinetry,
 * countertop, appliances, flooring, backsplash, painting, island)
 * explicitly distinguishes "not answered" (a real, expected case - falls
 * back to a sensible default/exclusion, same as always) from "answered
 * with a value that doesn't match any known option" (only reachable by a
 * caller bypassing the UI's fixed option list, but a real risk once a
 * second project type starts sharing option-id conventions). The latter
 * now always produces an explicit `..._unrecognized_selection` warning
 * plus an exclusion, instead of silently vanishing from the estimate
 * with no assumption, exclusion, or warning anywhere to explain why.
 */

// --- Cabinetry --------------------------------------------------------------

const CABINETRY_ITEM_BY_OPTION: Record<string, string | null> = {
  keep_existing: null, // no cabinetry cost at all - nothing is changing
  reface_existing: null, // no new cabinetry material - see exclusion below
  stock_new: "kitchen_cabinetry_stock",
  semi_custom_new: "kitchen_cabinetry_semi_custom",
  full_custom_new: "kitchen_cabinetry_full_custom"
};

// --- Countertops --------------------------------------------------------------

const COUNTERTOP_ITEM_BY_OPTION: Record<string, string | null> = {
  keep_existing: null,
  laminate: "kitchen_countertop_laminate",
  quartz: "kitchen_countertop_quartz",
  granite: "kitchen_countertop_granite",
  premium_custom: "kitchen_countertop_premium",
  undecided: null
};

// --- Appliances --------------------------------------------------------------

const APPLIANCE_ITEM_BY_TIER: Record<string, string | null> = {
  keep_existing: null,
  replace_some: "kitchen_appliances_partial",
  replace_all: "kitchen_appliances_package",
  premium: "kitchen_appliances_premium"
};

// --- Flooring --------------------------------------------------------------

const FLOORING_ITEM_BY_TIER: Record<string, string | null> = {
  not_changing: null,
  basic: "kitchen_flooring_basic",
  mid_range: "kitchen_flooring",
  premium: "kitchen_flooring_premium"
};

// --- Backsplash --------------------------------------------------------------

const BACKSPLASH_ITEM_BY_TIER: Record<string, string | null> = {
  no_change: null,
  standard: "kitchen_backsplash",
  full_height: "kitchen_backsplash_full_height",
  premium: "kitchen_backsplash_premium"
};

// --- Island --------------------------------------------------------------

const ISLAND_ITEM_BY_TIER: Record<string, string | null> = {
  no_island: null,
  existing_retained: null,
  new_standard: "kitchen_island",
  large_custom: "kitchen_island_large"
};

// --- Electrical / plumbing / structural tiers, with a flat-allowance fallback ---

const ELECTRICAL_ITEM_BY_TIER: Record<string, string> = {
  minor_updates: "kitchen_electrical_minor",
  new_lighting: "kitchen_electrical_lighting",
  additional_circuits: "kitchen_electrical_circuits",
  significant_changes: "kitchen_electrical"
};

const PLUMBING_ITEM_BY_TIER: Record<string, string> = {
  minor_changes: "kitchen_plumbing_minor",
  fixtures_relocated: "kitchen_plumbing_relocation",
  major_changes: "kitchen_plumbing"
};

const STRUCTURAL_ITEM_BY_TIER: Record<string, string> = {
  non_structural_wall_removal: "kitchen_structural_non_load_bearing",
  potential_structural_wall_removal: "kitchen_structural_changes",
  major_layout_changes: "kitchen_structural_major"
};

// --- Painting --------------------------------------------------------------

const PAINTING_ITEM_BY_TIER: Record<string, string | null> = {
  not_included: null,
  walls_only: "kitchen_painting_walls",
  full_room: "kitchen_painting_full"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

export const calculateKitchen: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;

  // --- Base renovation (anchor) + scope adjustment ---------------------
  let baseTypicalForPercentage = 0;
  if (sqft && sqft > 0) {
    const base = applyItem(catalog, "kitchen_base_renovation", { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);
      const scoped = scaleAppliedItem(base, scopeMultiplier);
      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base renovation cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });
    }
  } else {
    anchorMissing = true;
  }

  // --- Cabinetry (material selection, linear-foot methodology) ---------
  const cabinetryOption = rawString(input, "kitchen_cabinetry");
  const cabinetryRecognized = cabinetryOption === undefined || cabinetryOption in CABINETRY_ITEM_BY_OPTION;
  const cabinetryItemId = cabinetryOption ? CABINETRY_ITEM_BY_OPTION[cabinetryOption] : "kitchen_cabinetry_semi_custom";
  if (cabinetryOption === "keep_existing") {
    exclusions.push({ code: "excludes_cabinetry", message: "Cabinetry is being kept as-is - no cabinetry cost is included." });
  } else if (cabinetryOption === "reface_existing") {
    exclusions.push({
      code: "excludes_new_cabinetry",
      message: "Cabinet refacing was selected - no new cabinetry material cost is included, only what's captured in the base renovation labour allowance."
    });
  } else if (cabinetryOption && !cabinetryRecognized) {
    // A non-empty answer that doesn't match any known option - rather than
    // silently dropping the category (as this used to do), flag it so the
    // gap is visible instead of invisible. Not reachable through the
    // current UI's fixed option list, but reachable by any caller handing
    // the engine an EstimatorInput directly (see input-validation.ts).
    warnings.push({
      code: "kitchen_cabinetry_unrecognized_selection",
      message: `Cabinetry selection "${cabinetryOption}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_cabinetry",
      message: `Cabinetry selection "${cabinetryOption}" could not be matched to a pricing option - no cabinetry cost is included in this estimate.`
    });
  } else if (cabinetryItemId && sqft && sqft > 0) {
    const linearFeet = deriveKitchenCabinetryLinearFeet(sqft);
    const cabinetry = applyItem(catalog, cabinetryItemId, { quantity: linearFeet, finishLevel, regionalMultiplier });
    if (cabinetry) {
      applied.push(cabinetry);
      assumptions.push({
        code: "cabinetry_linear_feet_derived",
        message: `Cabinetry linear footage (${linearFeet} ft) was estimated from room size (${sqft} sqft), since linear footage isn't a direct input. Provide it directly in a later phase for more precision.`
      });
      if (!cabinetryOption) {
        assumptions.push({
          code: "cabinetry_selection_defaulted",
          message: 'No cabinetry selection was provided - defaulted to "semi-custom" for planning purposes.'
        });
      }
    }
  }

  // --- Countertops (material selection) ---------------------------------
  const countertopOption = rawString(input, "kitchen_countertop");
  const countertopRecognized = countertopOption === undefined || countertopOption in COUNTERTOP_ITEM_BY_OPTION;
  // Audit fix (Phase 23, Full Application Red-Team Audit Finding 4): no
  // implicit "quartz" fallback for a blank answer anymore - see below.
  const countertopItemId = countertopOption ? COUNTERTOP_ITEM_BY_OPTION[countertopOption] : undefined;
  if (countertopOption === "keep_existing") {
    exclusions.push({ code: "excludes_countertop", message: "Countertops are being kept as-is - no countertop cost is included." });
  } else if (countertopOption === "undecided" || countertopOption === undefined) {
    // Audit fix (Phase 23): leaving this question blank used to silently
    // price a full quartz countertop as a default, while explicitly
    // answering "not decided yet" excluded the cost - so the more honest
    // answer (admitting real uncertainty) produced a *cheaper* estimate
    // than simply skipping the question. Both are the same real-world
    // uncertainty and now get the same, disclosed treatment: excluded, not
    // guessed at. This intentionally changes the no-answer behavior from a
    // silent default to an explicit exclusion - see contingency.ts's
    // sibling fix in the same audit pass for the same "don't guess, disclose"
    // principle applied elsewhere.
    exclusions.push({
      code: "excludes_countertop",
      message:
        countertopOption === "undecided"
          ? "Countertop material was marked as not yet decided - excluded from this estimate."
          : "No countertop selection was provided - excluded from this estimate rather than assumed. Answer this question for a more complete estimate."
    });
  } else if (countertopOption && !countertopRecognized) {
    warnings.push({
      code: "kitchen_countertop_unrecognized_selection",
      message: `Countertop selection "${countertopOption}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_countertop",
      message: `Countertop selection "${countertopOption}" could not be matched to a pricing option - no countertop cost is included in this estimate.`
    });
  } else if (countertopItemId && sqft && sqft > 0) {
    // Countertop area is a fraction of total kitchen floor area, not equal to it.
    const countertopSqft = Math.max(15, Math.round(sqft * 0.18));
    const countertop = applyItem(catalog, countertopItemId, { quantity: countertopSqft, finishLevel, regionalMultiplier });
    if (countertop) {
      applied.push(countertop);
      assumptions.push({
        code: "countertop_area_derived",
        message: `Countertop area (${countertopSqft} sqft) was estimated as a fraction of total kitchen floor area (${sqft} sqft).`
      });
    }
  }

  // --- Appliances (discrete tier allowance) -------------------------------
  const applianceTier = rawString(input, "kitchen_appliances");
  const applianceRecognized = applianceTier === undefined || applianceTier in APPLIANCE_ITEM_BY_TIER;
  const applianceItemId = applianceTier ? APPLIANCE_ITEM_BY_TIER[applianceTier] : null;
  if (applianceItemId) {
    const appliances = applyItem(catalog, applianceItemId, { finishLevel, regionalMultiplier });
    if (appliances) applied.push(appliances);
  } else if (applianceTier === "keep_existing") {
    exclusions.push({ code: "excludes_appliances", message: "Appliances are being kept as-is - no appliance cost is included." });
  } else if (applianceTier && !applianceRecognized) {
    warnings.push({
      code: "kitchen_appliances_unrecognized_selection",
      message: `Appliance selection "${applianceTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_appliances",
      message: `Appliance selection "${applianceTier}" could not be matched to a pricing option - no appliance cost is included in this estimate.`
    });
  } else {
    // Truly unanswered (not "keep existing" - just skipped). Unlike the
    // flooring/backsplash "defaulted" pattern below, we don't assume a
    // replacement tier here (appliances range too widely in price to
    // guess a default), but we still record this as a defaulted
    // assumption so input-completeness scoring treats a skipped
    // appliances answer the same way it treats a skipped flooring or
    // backsplash answer, rather than silently counting it as "fully
    // specified."
    exclusions.push({
      code: "excludes_appliances",
      message: "No appliance selection was provided - assumed existing appliances are being kept, so no appliance cost is included."
    });
    assumptions.push({
      code: "appliances_defaulted",
      message: "No appliance selection was provided - assumed existing appliances are being kept for planning purposes."
    });
  }

  // --- Flooring (discrete tier, gated on selection) ------------------------
  const flooringTier = rawString(input, "kitchen_flooring_type");
  const flooringRecognized = flooringTier === undefined || flooringTier in FLOORING_ITEM_BY_TIER;
  const flooringItemId = flooringTier !== undefined ? FLOORING_ITEM_BY_TIER[flooringTier] : "kitchen_flooring";
  if (flooringTier === "not_changing") {
    exclusions.push({ code: "excludes_flooring", message: "Flooring is not changing - no flooring cost is included." });
  } else if (flooringTier && !flooringRecognized) {
    warnings.push({
      code: "kitchen_flooring_unrecognized_selection",
      message: `Flooring selection "${flooringTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_flooring",
      message: `Flooring selection "${flooringTier}" could not be matched to a pricing option - no flooring cost is included in this estimate.`
    });
  } else if (flooringItemId && sqft && sqft > 0) {
    const flooring = applyItem(catalog, flooringItemId, { quantity: sqft, finishLevel, regionalMultiplier });
    if (flooring) applied.push(flooring);
    if (flooringTier === undefined) {
      assumptions.push({ code: "flooring_defaulted", message: 'No flooring selection was provided - assumed a mid-range flooring replacement.' });
    }
  }

  // --- Backsplash (discrete tier, gated on selection) -----------------------
  const backsplashTier = rawString(input, "kitchen_backsplash_type");
  const backsplashRecognized = backsplashTier === undefined || backsplashTier in BACKSPLASH_ITEM_BY_TIER;
  const backsplashItemId = backsplashTier !== undefined ? BACKSPLASH_ITEM_BY_TIER[backsplashTier] : "kitchen_backsplash";
  if (backsplashTier === "no_change") {
    exclusions.push({ code: "excludes_backsplash", message: "Backsplash is not changing - no backsplash cost is included." });
  } else if (backsplashTier && !backsplashRecognized) {
    warnings.push({
      code: "kitchen_backsplash_unrecognized_selection",
      message: `Backsplash selection "${backsplashTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_backsplash",
      message: `Backsplash selection "${backsplashTier}" could not be matched to a pricing option - no backsplash cost is included in this estimate.`
    });
  } else if (backsplashItemId && sqft && sqft > 0) {
    // Full-height covers a larger band than a standard/premium backsplash.
    const backsplashSqft = Math.max(10, Math.round(sqft * (backsplashTier === "full_height" ? 0.28 : 0.12)));
    const backsplash = applyItem(catalog, backsplashItemId, { quantity: backsplashSqft, finishLevel, regionalMultiplier });
    if (backsplash) {
      applied.push(backsplash);
      assumptions.push({
        code: "backsplash_area_derived",
        message: `Backsplash area (${backsplashSqft} sqft) was estimated as a fraction of total kitchen floor area.`
      });
    }
    if (backsplashTier === undefined) {
      assumptions.push({ code: "backsplash_defaulted", message: 'No backsplash selection was provided - assumed a standard backsplash.' });
    }
  }

  // --- Painting (optional add-on) -------------------------------------------
  const paintingTier = rawString(input, "kitchen_painting");
  const paintingRecognized = paintingTier === undefined || paintingTier in PAINTING_ITEM_BY_TIER;
  const paintingItemId = paintingTier ? PAINTING_ITEM_BY_TIER[paintingTier] : null;
  if (paintingItemId && sqft && sqft > 0) {
    const painting = applyItem(catalog, paintingItemId, { quantity: sqft, finishLevel, regionalMultiplier });
    if (painting) applied.push(painting);
  } else if (!paintingTier || paintingTier === "not_included") {
    exclusions.push({ code: "excludes_painting", message: "Painting was not selected - excluded from this estimate." });
  } else if (!paintingRecognized) {
    warnings.push({
      code: "kitchen_painting_unrecognized_selection",
      message: `Painting selection "${paintingTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_painting",
      message: `Painting selection "${paintingTier}" could not be matched to a pricing option - no painting cost is included in this estimate.`
    });
  }

  // --- Island (discrete tier) -----------------------------------------------
  const islandTier = rawString(input, "kitchen_island");
  const islandRecognized = islandTier === undefined || islandTier in ISLAND_ITEM_BY_TIER;
  const islandItemId = islandTier ? ISLAND_ITEM_BY_TIER[islandTier] : null;
  if (islandItemId) {
    const island = applyItem(catalog, islandItemId, { finishLevel, regionalMultiplier });
    if (island) applied.push(island);
  } else if (islandTier && !islandRecognized) {
    warnings.push({
      code: "kitchen_island_unrecognized_selection",
      message: `Island selection "${islandTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
  }
  // A "large/custom" or "new standard" island needs real floor space to be
  // physically plausible - flag it (without dropping the cost, since the
  // user may genuinely be planning a layout change alongside it) when the
  // selected kitchen size can't realistically fit it.
  if (sqft && islandTier === "large_custom" && sqft < 120) {
    warnings.push({
      code: "kitchen_island_size_mismatch",
      message: `A large/custom island was selected for a ${sqft} sq. ft. kitchen - a large island typically needs significantly more clearance than that. This estimate includes the island cost as selected; please confirm the size is realistic for this space.`,
      severity: "caution"
    });
  } else if (sqft && islandTier === "new_standard" && sqft < 80) {
    warnings.push({
      code: "kitchen_island_size_mismatch",
      message: `A new island was selected for a ${sqft} sq. ft. kitchen - a standard island typically needs more clearance than that. This estimate includes the island cost as selected; please confirm the size is realistic for this space.`,
      severity: "caution"
    });
  }
  const layoutChange = rawString(input, "kitchen_layout_change");
  if (layoutChange === "keep_layout" && (islandTier === "new_standard" || islandTier === "large_custom")) {
    warnings.push({
      code: "kitchen_contradictory_layout_island",
      message:
        "You indicated you're keeping the current kitchen layout, but also selected a new island - adding an island typically requires a layout change. This estimate includes the island cost as selected; please confirm which is correct.",
      severity: "caution"
    });
  }

  // --- Work-type-conditional categories: only apply what's relevant, gated
  //     on the common yes/no/unknown flag, tiered by an optional follow-up.
  //     An "unknown" flag alone stays conservative (no cost added, same as
  //     Phase 2) - but an explicit tier answer is itself a confirmation, so
  //     it applies cost even if the common flag was left at "unknown". ---
  const plumbingTier = rawString(input, "kitchen_plumbing_scope");
  if (input.workType.plumbingChanges === "yes" || plumbingTier) {
    const plumbingItemId = plumbingTier ? PLUMBING_ITEM_BY_TIER[plumbingTier] : "kitchen_plumbing";
    const plumbing = plumbingItemId ? applyItem(catalog, plumbingItemId, { finishLevel, regionalMultiplier }) : null;
    if (plumbing) {
      applied.push(plumbing);
    } else if (input.workType.plumbingChanges === "yes") {
      warnings.push({
        code: "kitchen_plumbing_no_applicable_pricing",
        message: "Plumbing changes were indicated, but no applicable plumbing pricing could be applied for this configuration - consult a contractor directly for this scope.",
        severity: "caution"
      });
    }
  } else if (input.workType.plumbingChanges === "unknown") {
    // Left unconfirmed and no scope was ever given, so - correctly,
    // conservatively - no plumbing cost is in this number at all. Unlike
    // structural (which already warns on "unknown" via
    // buildInputDrivenWarnings), plumbing had no equivalent, so a user
    // could easily read past "not sure" without realizing plumbing costs
    // are entirely absent from their total.
    warnings.push({
      code: "kitchen_plumbing_scope_unknown",
      message: "Plumbing work wasn't confirmed, so no plumbing cost is included in this estimate. If plumbing changes turn out to be needed, this number will need to be revised.",
      severity: "caution"
    });
  }
  const electricalTier = rawString(input, "kitchen_electrical_scope");
  if (input.workType.electricalChanges === "yes" || electricalTier) {
    const electricalItemId = electricalTier ? ELECTRICAL_ITEM_BY_TIER[electricalTier] : "kitchen_electrical";
    const electrical = electricalItemId ? applyItem(catalog, electricalItemId, { finishLevel, regionalMultiplier }) : null;
    if (electrical) applied.push(electrical);
  } else if (input.workType.electricalChanges === "unknown") {
    warnings.push({
      code: "kitchen_electrical_scope_unknown",
      message: "Electrical work wasn't confirmed, so no electrical cost is included in this estimate. If electrical changes turn out to be needed, this number will need to be revised.",
      severity: "caution"
    });
  }
  const structuralTier = rawString(input, "kitchen_structural_scope");
  if (input.workType.structuralChanges === "yes" || structuralTier) {
    const structuralItemId = structuralTier ? STRUCTURAL_ITEM_BY_TIER[structuralTier] : "kitchen_structural_changes";
    const structural = structuralItemId ? applyItem(catalog, structuralItemId, { finishLevel, regionalMultiplier }) : null;
    if (structural) {
      applied.push(structural);
      assumptions.push({
        code: "kitchen_structural_engineering_flag",
        message:
          "Structural or load-bearing wall changes were selected. This estimate includes only a placeholder structural allowance - an engineer's assessment and site evaluation, and likely a structural permit, will be required before final pricing."
      });
    } else if (input.workType.structuralChanges === "yes") {
      warnings.push({
        code: "kitchen_structural_no_applicable_pricing",
        message: "Structural changes were indicated, but no applicable structural pricing could be applied for this configuration - an engineer's assessment is required before this scope can be budgeted.",
        severity: "caution"
      });
    }
  }
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "kitchen_demolition")) {
    const demolition = applyItem(catalog, "kitchen_demolition", {
      regionalMultiplier,
      baseAmountForPercentage: baseTypicalForPercentage
    });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility (optional) -------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "kitchen_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Permit / design allowance -------------------------------------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "kitchen_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
